import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { 
  ArrowLeft, Edit, Trash2, Save, Clock, CheckCircle, XCircle, Play,
  Truck, User, Calendar, Wrench, DollarSign, Camera, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';

const WorkOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [machinery, setMachinery] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [orderRes, machineryRes, usersRes] = await Promise.all([
        axios.get(`${API}/work-orders/${id}`),
        axios.get(`${API}/machinery`),
        axios.get(`${API}/users`).catch(() => ({ data: [] }))
      ]);
      setOrder(orderRes.data);
      setFormData(orderRes.data);
      setMachinery(machineryRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Error al cargar orden de trabajo');
      navigate('/work-orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/work-orders/${id}`, {
        description: formData.description,
        priority: formData.priority,
        work_type: formData.work_type,
        supervisor_id: formData.supervisor_id,
        mechanic_id: formData.mechanic_id,
        helper_id: formData.helper_id,
        estimated_hours: formData.estimated_hours,
        actual_hours: formData.actual_hours,
        scheduled_date: formData.scheduled_date,
        work_performed: formData.work_performed,
        labor_cost: formData.labor_cost,
        parts_cost: formData.parts_cost,
        notes: formData.notes
      });
      toast.success('Orden actualizada');
      setEditing(false);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(`${API}/work-orders/${id}/status?status=${newStatus}`);
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar esta orden de trabajo?')) return;
    try {
      await axios.delete(`${API}/work-orders/${id}`);
      toast.success('Orden eliminada');
      navigate('/work-orders');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataImg = new FormData();
    formDataImg.append('file', file);

    try {
      await axios.post(`${API}/work-orders/${id}/images`, formDataImg);
      toast.success('Imagen subida');
      fetchData();
    } catch (error) {
      toast.error('Error al subir imagen');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-700', label: 'Pendiente', icon: Clock },
      in_progress: { color: 'bg-blue-100 text-blue-700', label: 'En Proceso', icon: Play },
      completed: { color: 'bg-green-100 text-green-700', label: 'Finalizada', icon: CheckCircle },
      cancelled: { color: 'bg-red-100 text-red-700', label: 'Cancelada', icon: XCircle }
    };
    const { color, label, icon: Icon } = config[status] || config.pending;
    return (
      <span className={`status-badge ${color} flex items-center text-lg px-4 py-2`}>
        <Icon size={18} className="mr-2" />
        {label}
      </span>
    );
  };

  if (loading) {
    return <div className="text-center py-12">Cargando...</div>;
  }

  if (!order) {
    return <div className="text-center py-12">Orden no encontrada</div>;
  }

  return (
    <div data-testid="work-order-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/work-orders')}
            className="p-2 hover:bg-slate-100 rounded mr-4"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <div className="flex items-center space-x-4">
              <h1 className="text-4xl font-bold text-orange-600 mono" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {order.order_number}
              </h1>
              {getStatusBadge(order.status)}
            </div>
            <p className="text-slate-600 mt-1">Creada por {order.created_by_name} el {new Date(order.created_at).toLocaleDateString()}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Status Actions */}
          {order.status === 'pending' && (
            <Button onClick={() => handleStatusChange('in_progress')} className="bg-blue-500 hover:bg-blue-600 text-white">
              <Play size={18} className="mr-2" />
              Iniciar Trabajo
            </Button>
          )}
          {order.status === 'in_progress' && (
            <Button onClick={() => handleStatusChange('completed')} className="bg-green-500 hover:bg-green-600 text-white">
              <CheckCircle size={18} className="mr-2" />
              Finalizar
            </Button>
          )}
          {order.status !== 'completed' && order.status !== 'cancelled' && (
            <Button onClick={() => handleStatusChange('cancelled')} className="bg-red-500 hover:bg-red-600 text-white">
              <XCircle size={18} className="mr-2" />
              Cancelar
            </Button>
          )}
          
          {editing ? (
            <Button onClick={handleSave} className="btn-primary">
              <Save size={18} className="mr-2" />
              Guardar
            </Button>
          ) : (
            <Button onClick={() => setEditing(true)} className="btn-secondary">
              <Edit size={18} className="mr-2" />
              Editar
            </Button>
          )}
          <Button onClick={handleDelete} className="bg-red-100 hover:bg-red-200 text-red-700">
            <Trash2 size={18} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <FileText size={20} className="mr-2 text-orange-500" />
              Descripción del Trabajo
            </h2>
            {editing ? (
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-field"
                rows={4}
              />
            ) : (
              <p className="text-slate-700">{order.description}</p>
            )}
          </div>

          {/* Work Performed */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <Wrench size={20} className="mr-2 text-orange-500" />
              Trabajo Realizado
            </h2>
            {editing ? (
              <textarea
                value={formData.work_performed || ''}
                onChange={(e) => setFormData({ ...formData, work_performed: e.target.value })}
                className="input-field"
                rows={4}
                placeholder="Describa el trabajo realizado..."
              />
            ) : (
              <p className="text-slate-700">{order.work_performed || 'Sin registrar aún'}</p>
            )}
          </div>

          {/* Personnel */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <User size={20} className="mr-2 text-orange-500" />
              Personal Asignado
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">Supervisor</label>
                {editing ? (
                  <select
                    value={formData.supervisor_id || ''}
                    onChange={(e) => setFormData({ ...formData, supervisor_id: e.target.value })}
                    className="input-field"
                  >
                    <option value="">Seleccionar...</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                ) : (
                  <p className="font-medium">{order.supervisor_name || '-'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">Mecánico</label>
                {editing ? (
                  <select
                    value={formData.mechanic_id || ''}
                    onChange={(e) => setFormData({ ...formData, mechanic_id: e.target.value })}
                    className="input-field"
                  >
                    <option value="">Seleccionar...</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                ) : (
                  <p className="font-medium">{order.mechanic_name || '-'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">Ayudante</label>
                {editing ? (
                  <select
                    value={formData.helper_id || ''}
                    onChange={(e) => setFormData({ ...formData, helper_id: e.target.value })}
                    className="input-field"
                  >
                    <option value="">Seleccionar...</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                ) : (
                  <p className="font-medium">{order.helper_name || '-'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <Camera size={20} className="mr-2 text-orange-500" />
              Fotos del Trabajo
            </h2>
            <div className="grid grid-cols-3 gap-4 mb-4">
              {order.images?.map((img, idx) => (
                <div key={idx} className="aspect-video bg-slate-100 rounded overflow-hidden">
                  <img src={img} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <label className="flex items-center justify-center border-2 border-dashed border-slate-300 rounded-md p-4 cursor-pointer hover:border-orange-400 transition-colors">
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              <Camera size={20} className="text-slate-400 mr-2" />
              <span className="text-slate-600">Agregar Foto</span>
            </label>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Machinery */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <Truck size={20} className="mr-2 text-orange-500" />
              Maquinaria
            </h2>
            <p className="font-medium text-slate-800">{order.machinery_name}</p>
          </div>

          {/* Times */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <Clock size={20} className="mr-2 text-orange-500" />
              Tiempos
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Fecha Programada:</span>
                {editing ? (
                  <input
                    type="date"
                    value={formData.scheduled_date || ''}
                    onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                    className="input-field w-40"
                  />
                ) : (
                  <span className="font-medium">{order.scheduled_date ? new Date(order.scheduled_date).toLocaleDateString() : '-'}</span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Inicio:</span>
                <span className="font-medium">{order.start_date ? new Date(order.start_date).toLocaleString() : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Fin:</span>
                <span className="font-medium">{order.end_date ? new Date(order.end_date).toLocaleString() : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Horas Estimadas:</span>
                {editing ? (
                  <input
                    type="number"
                    step="0.5"
                    value={formData.estimated_hours || ''}
                    onChange={(e) => setFormData({ ...formData, estimated_hours: parseFloat(e.target.value) || null })}
                    className="input-field w-20"
                  />
                ) : (
                  <span className="font-medium mono">{order.estimated_hours || '-'}</span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Horas Reales:</span>
                {editing ? (
                  <input
                    type="number"
                    step="0.5"
                    value={formData.actual_hours || ''}
                    onChange={(e) => setFormData({ ...formData, actual_hours: parseFloat(e.target.value) || null })}
                    className="input-field w-20"
                  />
                ) : (
                  <span className="font-medium mono">{order.actual_hours || '-'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Costs */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <DollarSign size={20} className="mr-2 text-orange-500" />
              Costos
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Mano de Obra:</span>
                {editing ? (
                  <input
                    type="number"
                    step="0.01"
                    value={formData.labor_cost || ''}
                    onChange={(e) => setFormData({ ...formData, labor_cost: parseFloat(e.target.value) || 0 })}
                    className="input-field w-28"
                  />
                ) : (
                  <span className="font-medium mono">Bs. {(order.labor_cost || 0).toFixed(2)}</span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Repuestos:</span>
                {editing ? (
                  <input
                    type="number"
                    step="0.01"
                    value={formData.parts_cost || ''}
                    onChange={(e) => setFormData({ ...formData, parts_cost: parseFloat(e.target.value) || 0 })}
                    className="input-field w-28"
                  />
                ) : (
                  <span className="font-medium mono">Bs. {(order.parts_cost || 0).toFixed(2)}</span>
                )}
              </div>
              <div className="flex justify-between pt-3 border-t border-slate-200">
                <span className="font-semibold text-slate-800">Total:</span>
                <span className="font-bold text-xl mono text-orange-600">Bs. {order.total_cost.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Priority & Type */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Clasificación</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-slate-600 mb-1">Prioridad</label>
                {editing ? (
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="input-field"
                  >
                    <option value="low">Baja</option>
                    <option value="normal">Normal</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>
                ) : (
                  <span className={`status-badge ${
                    order.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                    order.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                    order.priority === 'normal' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {order.priority === 'urgent' ? 'Urgente' :
                     order.priority === 'high' ? 'Alta' :
                     order.priority === 'normal' ? 'Normal' : 'Baja'}
                  </span>
                )}
              </div>
              <div>
                <label className="block text-sm text-slate-600 mb-1">Tipo de Trabajo</label>
                {editing ? (
                  <select
                    value={formData.work_type}
                    onChange={(e) => setFormData({ ...formData, work_type: e.target.value })}
                    className="input-field"
                  >
                    <option value="preventive">Preventivo</option>
                    <option value="corrective">Correctivo</option>
                    <option value="predictive">Predictivo</option>
                  </select>
                ) : (
                  <span className={`status-badge ${
                    order.work_type === 'preventive' ? 'bg-green-100 text-green-700' :
                    order.work_type === 'predictive' ? 'bg-purple-100 text-purple-700' :
                    'bg-orange-100 text-orange-700'
                  }`}>
                    {order.work_type === 'preventive' ? 'Preventivo' :
                     order.work_type === 'predictive' ? 'Predictivo' : 'Correctivo'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          {(order.notes || editing) && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Notas</h2>
              {editing ? (
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-field"
                  rows={3}
                />
              ) : (
                <p className="text-slate-700">{order.notes}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkOrderDetail;
